import { useState } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, CheckCircle, Package } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api from "@/utils/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "picked_up", label: "Picked Up", desc: "Package collected from sender" },
  { value: "in_transit", label: "In Transit", desc: "Package is on the way" },
  { value: "out_for_delivery", label: "Out for Delivery", desc: "Package with delivery agent" },
  { value: "delivered", label: "Delivered", desc: "Package delivered to receiver" },
  { value: "cancelled", label: "Cancelled", desc: "Shipment cancelled" },
];

export default function UpdateStatus() {
  const [trackingId, setTrackingId] = useState("");
  const [shipment, setShipment] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [note, setNote] = useState("");
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    setSearching(true);
    setError("");
    setShipment(null);
    setSelectedStatus("");
    setNote("");
    try {
      const res = await api.get("/shipments", { params: { search: trackingId.trim().toUpperCase() } });
      const items = res.data.items || [];
      const found = items.find((s) => s.tracking_id === trackingId.trim().toUpperCase());
      if (found) {
        setShipment(found);
        setSelectedStatus(found.status);
      } else {
        setError("Shipment not found. Please check the tracking ID.");
      }
    } catch {
      setError("Error searching. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const handleUpdate = async () => {
    if (!shipment || !selectedStatus) return;
    setUpdating(true);
    try {
      await api.put(`/shipments/${shipment.id}/status`, { status: selectedStatus, note });
      toast.success(`Status updated to "${selectedStatus.replace(/_/g, " ")}"`);
      setShipment({ ...shipment, status: selectedStatus });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Update Status</h1>
          <p className="text-slate-500 text-sm">Search a shipment by tracking ID and update its status.</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-lg border border-slate-200 p-5 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={trackingId} onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
              placeholder="Enter Tracking ID (e.g. STXABC12345)"
              data-testid="status-tracking-search"
              className="w-full pl-9 pr-3 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 font-heading font-semibold" />
          </div>
          <button type="submit" disabled={searching} data-testid="status-search-btn"
            className="px-5 py-3 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70 flex items-center gap-2">
            <Search className="w-4 h-4" /> {searching ? "Searching..." : "Find"}
          </button>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
        )}

        {shipment && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {/* Shipment Card */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-slate-400 font-semibold uppercase">Tracking ID</p>
                  <h3 className="font-heading font-bold text-xl text-brand-500">{shipment.tracking_id}</h3>
                </div>
                <StatusBadge status={shipment.status} />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold mb-1">From</p>
                  <p className="text-slate-700 font-medium">{shipment.origin?.name}</p>
                  <p className="text-xs text-slate-500">{shipment.origin?.city}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold mb-1">To</p>
                  <p className="text-slate-700 font-medium">{shipment.destination?.name}</p>
                  <p className="text-xs text-slate-500">{shipment.destination?.city}</p>
                </div>
              </div>
            </div>

            {/* Status Update */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-lg text-slate-800 uppercase mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" /> Update Status
              </h3>
              <div className="grid grid-cols-1 gap-2 mb-4">
                {STATUS_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => setSelectedStatus(opt.value)}
                    data-testid={`status-option-${opt.value}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all min-h-[56px] ${selectedStatus === opt.value ? "border-brand-500 bg-brand-50" : "border-slate-100 hover:border-slate-300"}`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedStatus === opt.value ? "border-brand-500" : "border-slate-300"}`}>
                      {selectedStatus === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${selectedStatus === opt.value ? "text-brand-500" : "text-slate-700"}`}>{opt.label}</p>
                      <p className="text-xs text-slate-400">{opt.desc}</p>
                    </div>
                    {shipment.status === opt.value && <span className="ml-auto text-xs font-bold text-slate-400">Current</span>}
                  </button>
                ))}
              </div>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Note (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder="Add a note for this status update..."
                  data-testid="status-note-input"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 resize-none" />
              </div>
              <button onClick={handleUpdate} disabled={updating || selectedStatus === shipment.status}
                data-testid="status-update-btn"
                className="w-full py-3 bg-orange-500 text-white font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 uppercase">
                <CheckCircle className="w-4 h-4" /> {updating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
